<template>
  <div class="login-signin">
    <b-form class="form" id="kt_login_signin_form" @submit.stop.prevent="onSubmit">
      <div
          v-if="errorList.length > 0"
          role="alert"
          v-bind:class="{ show: errorList.length }"
          class="alert fade alert-danger"
      >
        <div class="alert-text" v-for="(error, i) in errorList" :key="i">
          {{ error.message }}
        </div>
      </div>

      <template v-if="!requiresVerification">
        <b-form-input
            class="mb-3 form-control h-auto text-black bg-white-o-5 rounded-pill border-dark py-4 px-8 login-input"
            id="example-input-1"
            name="example-input-1"
            placeholder="Email"
            v-model="$v.form.email.$model"
            :state="validateState('email')"
            aria-describedby="input-1-live-feedback"
        ></b-form-input>

        <b-form-invalid-feedback id="input-1-live-feedback" class="mb-3 error-div">
          A valid email is required.
        </b-form-invalid-feedback>

        <b-form-input
            class="mb-3 form-control h-auto text-black bg-white-o-5 rounded-pill border-dark py-4 px-8 login-input"
            :type="show ? 'text' : 'password'"
            id="example-input-2"
            name="example-input-2"
            placeholder="Password"
            v-model="$v.form.password.$model"
            :state="validateState('password')"
            aria-describedby="input-2-live-feedback"
        ></b-form-input>

        <b-form-invalid-feedback id="input-2-live-feedback" class="mb-3 error-div">
          Password is required.
        </b-form-invalid-feedback>

        <div class="form-group d-flex flex-wrap justify-content-between align-items-center px-1 opacity-60">
          <div class="checkbox-inline"></div>
          <router-link
              style="color: #000000"
              class="font-weight-bold"
              :to="{ name: 'forgot-password' }"
          >
            Forgot Password?
          </router-link>
        </div>
      </template>

      <template v-else>
        <div
            role="alert"
            class="alert fade alert-info show mb-4"
        >
          <div class="alert-text">
            A 6-digit verification code has been sent to your email. Enter it below to continue.
          </div>
        </div>

        <div class="code-inputs mb-3" :class="{ 'code-inputs-loading': verificationLoading }">
          <input
              v-for="(digit, index) in codeDigits"
              :key="index"
              :ref="'codeInput' + index"
              v-model="codeDigits[index]"
              class="code-input-box"
              type="text"
              inputmode="numeric"
              maxlength="1"
              autocomplete="one-time-code"
              :disabled="verificationLoading"
              @input="onCodeInput(index, $event)"
              @keydown="onCodeKeydown(index, $event)"
              @paste="onCodePaste($event)"
          />
        </div>

        <div v-if="verificationLoading" class="text-center mb-3 verification-loading-text">
          Verifying...
        </div>

        <div class="text-center mb-4 verification-timer">
          <span v-if="!canResendCode" class="timer-text">
            Resend code in {{ formattedCountdown }}
          </span>
          <button
              v-else
              type="button"
              class="btn btn-link p-0 resend-btn"
              :disabled="resendLoading || verificationLoading"
              @click="onResendCode"
          >
            {{ resendLoading ? 'Sending...' : 'Resend code' }}
          </button>
        </div>
      </template>

      <div v-if="!requiresVerification" class="form-group text-center mt-5">
        <button ref="kt_login_signin_submit" class="btn btn-pill btn-primary opacity-90 px-15 py-3">
          Sign In
        </button>
      </div>
    </b-form>
  </div>
</template>

<style lang="scss" scoped>
.spinner.spinner-right {
  padding-right: 3.5rem !important;
}

.login-input {
  background-color: rgba(0, 0, 0, 0.1) !important;
}

.login-input::placeholder {
  color: #000000;
  opacity: 1;
}

.login-input::-ms-input-placeholder {
  color: #000000;
}

.error-div {
  color: #ffffff;
  background-color: #F64E60;
  padding: 5px 5px;
  margin-top: 5px;
  border-radius: 9999px;
  text-align: center;
}

.code-inputs {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.code-input-box {
  width: 46px;
  height: 52px;
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  color: #000000;
  background-color: rgba(0, 0, 0, 0.1);
  border: 1px solid #343a40;
  border-radius: 12px;
  outline: none;
  padding: 0;

  &:focus {
    border-color: #163891;
    box-shadow: 0 0 0 2px rgba(22, 56, 145, 0.15);
  }

  &:disabled {
    opacity: 0.6;
  }
}

.code-inputs-loading {
  opacity: 0.7;
  pointer-events: none;
}

.verification-loading-text {
  color: #163891;
  font-weight: 600;
  font-size: 14px;
}

.verification-timer {
  .timer-text {
    color: #000000;
    font-weight: 600;
    font-size: 14px;
  }

  .resend-btn {
    color: #163891;
    font-weight: 700;
    font-size: 14px;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }

    &:disabled {
      opacity: 0.6;
    }
  }
}
</style>

<script>
import {mapState} from "vuex";
import {
  LOGIN,
  CLEAR_AUTH_ERRORS,
  RESEND_LOGIN_CODE
} from "@/core/services/store/auth.module";
import {SET_SNACK} from "@/core/services/store/snackbar.module";

import {validationMixin} from "vuelidate";
import {email, minLength, required} from "vuelidate/lib/validators";

export default {
  mixins: [validationMixin],
  name: "login",
  data() {
    return {
      show: false,
      requiresVerification: false,
      verificationLoading: false,
      resendCountdown: 60,
      resendTimerId: null,
      canResendCode: false,
      resendLoading: false,
      codeDigits: ["", "", "", "", "", ""],
      form: {
        email: "",
        password: "",
        verificationCode: ""
      }
    };
  },
  validations() {
    return {
      form: {
        email: {
          required,
          email
        },
        password: {
          required,
          minLength: minLength(3)
        }
      }
    };
  },
  computed: {
    ...mapState({
      errors: state => state.auth.errors
    }),
    errorList() {
      const errors = this.errors;
      if (Array.isArray(errors)) {
        return errors;
      }
      if (errors && typeof errors === "object") {
        return Object.keys(errors).map((key) => {
          const value = errors[key];
          if (value && typeof value === "object" && value.message) {
            return value;
          }
          return { message: Array.isArray(value) ? value[0] : String(value) };
        });
      }
      return [];
    },
    formattedCountdown() {
      const minutes = Math.floor(this.resendCountdown / 60);
      const seconds = this.resendCountdown % 60;
      return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    }
  },
  beforeDestroy() {
    this.clearResendTimer();
  },
  methods: {
    validateState(name) {
      const field = this.$v.form[name];
      if (!field) {
        return null;
      }
      const {$dirty, $error} = field;
      return $dirty ? !$error : null;
    },
    resetCodeDigits() {
      this.codeDigits = ["", "", "", "", "", ""];
      this.form.verificationCode = "";
    },
    focusCodeInput(index) {
      this.$nextTick(() => {
        const ref = this.$refs["codeInput" + index];
        const el = Array.isArray(ref) ? ref[0] : ref;
        if (el) {
          el.focus();
          el.select();
        }
      });
    },
    onCodeInput(index, event) {
      const value = event.target.value.replace(/\D/g, "");
      this.$set(this.codeDigits, index, value.slice(-1));

      if (value && index < 5) {
        this.focusCodeInput(index + 1);
      }

      if (this.codeDigits.every((digit) => digit !== "")) {
        this.submitVerification();
      }
    },
    onCodeKeydown(index, event) {
      if (event.key === "Backspace" && !this.codeDigits[index] && index > 0) {
        this.$set(this.codeDigits, index - 1, "");
        this.focusCodeInput(index - 1);
        event.preventDefault();
      }
    },
    onCodePaste(event) {
      event.preventDefault();
      const pasted = (event.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
      if (!pasted) {
        return;
      }

      pasted.split("").forEach((digit, index) => {
        this.$set(this.codeDigits, index, digit);
      });

      for (let i = pasted.length; i < 6; i++) {
        this.$set(this.codeDigits, i, "");
      }

      if (pasted.length === 6) {
        this.submitVerification();
      } else {
        this.focusCodeInput(Math.min(pasted.length, 5));
      }
    },
    setSubmitLoading(isLoading) {
      const submitButton = this.$refs["kt_login_signin_submit"];
      if (!submitButton) {
        return;
      }

      if (isLoading) {
        submitButton.classList.add("spinner", "spinner-light", "spinner-right");
        submitButton.disabled = true;
      } else {
        submitButton.classList.remove("spinner", "spinner-light", "spinner-right");
        submitButton.disabled = false;
      }
    },
    clearResendTimer() {
      if (this.resendTimerId) {
        clearInterval(this.resendTimerId);
        this.resendTimerId = null;
      }
    },
    startResendTimer() {
      this.clearResendTimer();
      this.resendCountdown = 60;
      this.canResendCode = false;

      this.resendTimerId = setInterval(() => {
        if (this.resendCountdown > 1) {
          this.resendCountdown--;
        } else {
          this.resendCountdown = 0;
          this.canResendCode = true;
          this.clearResendTimer();
        }
      }, 1000);
    },
    onResendCode() {
      if (!this.canResendCode || this.resendLoading || this.verificationLoading) {
        return;
      }

      this.$store.dispatch(CLEAR_AUTH_ERRORS);
      this.resendLoading = true;

      this.$store
          .dispatch(RESEND_LOGIN_CODE, {
            email: this.form.email,
            password: this.form.password
          })
          .then((data) => {
            if (data._metadata.outcomeCode === 0) {
              this.resetCodeDigits();
              this.$store.dispatch(SET_SNACK, {
                clr: "success",
                msg: data._metadata.message
              });
              this.startResendTimer();
              this.focusCodeInput(0);
            }
          })
          .finally(() => {
            this.resendLoading = false;
          });
    },
    submitVerification() {
      const code = this.codeDigits.join("");
      if (code.length !== 6 || this.verificationLoading) {
        return;
      }

      this.form.verificationCode = code;

      const payload = {
        email: this.form.email,
        password: this.form.password,
        verification_code: code
      };

      this.$store.dispatch(CLEAR_AUTH_ERRORS);
      this.verificationLoading = true;

      this.$store
          .dispatch(LOGIN, payload)
          .then((data) => {
            if (data._metadata.outcomeCode === 0) {
              this.clearResendTimer();
              this.$router.push({path: "/"});
              return;
            }

            this.resetCodeDigits();
            this.focusCodeInput(0);
          })
          .catch(() => {
            this.resetCodeDigits();
            this.focusCodeInput(0);
          })
          .finally(() => {
            this.verificationLoading = false;
          });
    },
    onSubmit() {
      if (this.requiresVerification) {
        return;
      }

      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      }

      const email = this.$v.form.email.$model;
      const password = this.$v.form.password.$model;
      const payload = {email, password};

      this.$store.dispatch(CLEAR_AUTH_ERRORS);
      this.setSubmitLoading(true);

      this.$store
          .dispatch(LOGIN, payload)
          .then((data) => {
            if (data._metadata.outcomeCode === 10) {
              this.requiresVerification = true;
              this.resetCodeDigits();
              this.startResendTimer();
              this.focusCodeInput(0);
              this.setSubmitLoading(false);
              return;
            }

            if (data._metadata.outcomeCode === 0) {
              this.$router.push({path: "/"});
            }

            this.setSubmitLoading(false);
          })
          .catch(() => {
            this.setSubmitLoading(false);
          });
    }
  }
};
</script>
